/**
 * @file TenantSSOStatusDropdown.js
 * @description A dropdown component for selecting tenant SSO statuses.
 */

import React, { useState, useEffect } from "react";
import { fetchTenantSSOStatuses } from "../../../services/ssoService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/** 
 * Represents a dropdown component for selecting tenant SSO statuses.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedStatus] - The ID of the selected tenant SSO status.
 * @param {function} props.onStatusChange - The callback function to be called when the selected status changes.
 * @param {string} [props.label="Tenant SSO Status"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @returns {JSX.Element} The rendered TenantSSOStatusDropdown component.
 */
const TenantSSOStatusDropdown = ({
  className,
  inlineLabel = true,
  selectedStatus,
  onStatusChange,
  label = "Tenant SSO Status",
  isRequired = false,
  allowNullSelection = false,
  disabled = false,
}) => {
  const [ssoStatuses, setSsoStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getSsoStatuses = async () => {
      setIsLoading(true);
      try {
        const data = await fetchTenantSSOStatuses();
        setSsoStatuses(data);
      } catch (error) {
        console.error("Could not fetch tenant SSO statuses:", error);
        Toast.error(getText('msg_0e0565b3cc02'));
      } finally {
        setIsLoading(false);
      }
    };

    getSsoStatuses();
  }, []);

  const getDefaultLabel = () => {
    const matchingStatus = ssoStatuses.find(
      (status) => status.id === selectedStatus
    );
    return matchingStatus ? matchingStatus.name : "Select Tenant SSO Status";
  };

  const statusOptions = allowNullSelection
    ? [
        { value: null, label: getText('msg_1a597d08bd95') },
        ...ssoStatuses.map((status) => ({
          value: status.id,
          label: status.name,
        })),
      ]
    : ssoStatuses.map((status) => ({
        value: status.id,
        label: status.name,
      }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="tenant-sso-status-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="tenant-sso-status-dropdown"
          defaultValue={getDefaultLabel()}
          options={statusOptions}
          value={selectedStatus}
          onChange={onStatusChange}
          disabled={ssoStatuses.length === 0 || isLoading || disabled}
        />
      </LoadingWrapper>
    </div>
  );
};

export default TenantSSOStatusDropdown;
